import React, { useState, forwardRef, useEffect, useRef, ReactElement, SyntheticEvent } from 'react';
import { DatePickerProps, SharedDatePickerProps, CustomHeaderProps } from './datePicker.types';
import {
  DatePickerWrapper,
  DatePickerWrapperCustomDays,
  ButtonWrapper,
  MobileButtonWrapper,
  InputLabel,
  InputWrapper,
  DatePickerStyle,
  DPWrapperProps,
  MiniYearWrapper,
  YearRowWrapper,
  YearSelectWrapper,
  MiniYearButtonWrapper,
  DateLabel,
} from './datePicker.style';
import ReactDatePicker from 'react-datepicker';
import Button from '../Button/button';
import { ButtonShapes, ButtonSizes } from '../Button/button.types';
import es from 'date-fns/locale/es';
import enUS from 'date-fns/locale/en-US';
import { useTheme } from 'styled-components';
import bindHandlers from '../../utils/handlerHelper';
import Handler from '../../commonTypes/handler.types';
import Icon from '../Icon/icon';
import { IconSizes } from '../Icon/icon.types';
import COLORS from '../../style/universal.color';
import BREAKPOINTS from '../../style/breakpoints';
import { useMedia } from '../../utils/hooks/useMedia/useMedia';
import MobileDatePicker from './mobileDatePicker';
import makeSafeDate from '../../utils/makeSafeDate';
import format from 'date-fns/format';

const { linkBlue, linkBlueOnDark, mlbGrey3 } = COLORS;
const noop = function () {};

// As far back as our data goes
const minimumYearDefault: number = 1903;

const textDateDisplayEn: string = 'EEEE MMM dd';
const textDateDisplayEs: string = "EEEE dd 'de' MMM";

// An object that keeps track of five year chunks for styling
interface DecadeYearsObj {
  yearArr: number[];
  midDecade?: boolean;
  endDecade?: boolean;
}

// Generates years to be used by the year-picker menu.
// Returns:
// fullYears - an array of years
// decadeYears - a custom array of five year sets for easy logic and rendering of decades
// currentYearIndex - the index of the current year in the full array
const generateFullYears = (selectedDate: Date, minDate: Date | undefined, maxDate: Date | undefined) => {
  const currentSelectedYear: number = selectedDate.getFullYear();

  // Get our min and max boundaries of years
  const furthestPastYear: number = minDate ? minDate.getFullYear() : minimumYearDefault;
  const furthestFutureYear: number = maxDate ? maxDate.getFullYear() : new Date().getFullYear() + 1;

  const fullYears: number[] = []; // simple array to store years
  const decadeYears: DecadeYearsObj[] = []; // array of DecadeYearsObj objects. See interface above ^^
  let currentYearIndex = -1; // Will keep track of where the current year is in fullYearrs
  let index = 0;

  // Keep track of groups of five (or smaller) and will be added
  // to decadeYears when ready. Then reset.
  let tempFiveYears: number[] = [];

  for (let year: number = furthestPastYear; year <= furthestFutureYear; year++) {
    fullYears.push(year); // Add the year to full Years
    tempFiveYears.push(year);
    if (year % 10 === 9) {
      // if the year ends with 9, mark end of decade
      decadeYears.push({
        yearArr: tempFiveYears,
        endDecade: true,
      });
      tempFiveYears = [];
    } else if (year % 10 === 4) {
      // if the year ends with 4, mark end of first half of decade
      decadeYears.push({
        yearArr: tempFiveYears,
        midDecade: true,
      });
      tempFiveYears = [];
    }

    // set current year index if applicable
    if (year === currentSelectedYear) {
      currentYearIndex = index;
    }
    index++;
  }

  // If there are leftover years, push them without worrying about where in decade they fall
  if (tempFiveYears.length > 0) {
    decadeYears.push({
      yearArr: tempFiveYears,
    });
  }

  return {
    decadeYears,
    fullYears,
    currentYearIndex,
  };
};

// Generate the slice of years to appear under calendar month for quick select
// Will show years around currently selected date
const generateMiniYears = (fullYears: number[], activeDate: Date, modMinDate?: Date, modMaxDate?: Date) => {
  if (fullYears.length <= 5) {
    return fullYears;
  }

  const activeYear: number = activeDate.getFullYear();
  const maxYear: number = modMaxDate ? modMaxDate.getFullYear() : new Date().getFullYear() + 1;
  const minYear: number = modMinDate ? modMinDate.getFullYear() : minimumYearDefault;

  if (maxYear - activeYear < 3) {
    return [maxYear - 4, maxYear - 3, maxYear - 2, maxYear - 1, maxYear];
  } else if (activeYear - minYear < 3) {
    return [minYear, minYear + 1, minYear + 2, minYear + 3, minYear + 4];
  } else {
    return [activeYear - 2, activeYear - 1, activeYear, activeYear + 1, activeYear + 2];
  }
};

const injectYearString = (label: string, year: number) => label.replace(/{{year}}/g, year.toString());

// Will create a row of year buttons inside the year picker
// Styles rows according to where in decade they fall
// Styles buttons according to dark theme, if current year, or is active (viewed) year
const createYearRow = (
  rowObj: { yearArr: number[]; midDecade?: boolean; endDecade?: boolean },
  handlers: Handler,
  i: number,
  currentYear: number,
  activeYear: number,
  dark: boolean | undefined,
  viewYearAriaLabel: string,
) => {
  const { yearArr = [], midDecade = false, endDecade = false } = rowObj;

  return (
    <YearRowWrapper index={i} midDecade={midDecade} endDecade={endDecade} key={`yearRow-${i}`}>
      {yearArr.map((year: number) => (
        <MiniYearButtonWrapper
          isCurrentYear={year === currentYear}
          isActiveYear={year === activeYear}
          fitWidth={true}
          dark={dark ?? false}
          key={`yearButtonWrapper-${year}`}
        >
          <Button
            shape={ButtonShapes.HOLLOW_BORDERLESS}
            value={year}
            handlers={handlers}
            ariaLabel={injectYearString(viewYearAriaLabel, year)}
            id={`yearPicker-${year}`}
          >
            {year}
          </Button>
        </MiniYearButtonWrapper>
      ))}
    </YearRowWrapper>
  );
};

const fetchIconFillColor = (disabled: boolean, dark?: boolean, iconFillColorOverride?: string): string => {
  if (iconFillColorOverride) {
    return iconFillColorOverride;
  } else if (disabled) {
    return mlbGrey3;
  } else {
    return dark ? linkBlueOnDark : linkBlue;
  }
};

const DatePicker = (props: DatePickerProps) => {
  const {
    selectedDate = new Date(),
    maxDate,
    minDate,
    inline, // key navigation DOES NOT WORK with inline prop known bug on https://github.com/Hacker0x01/react-datepicker/issues/744
    lang = 'en',
    locale: localeOverride,
    todayLabel = 'Today',
    daysHeaderLabels,
    includedDates,
    excludedDates = [],
    closeOnSelect = true,
    disabled = false,
    handlers = {},
    triggerSize = IconSizes.LG,
    openLeft = false,
    openLeftMin = 'xs',
    openLeftMax,
    isMobile,
    breakpoint = 'sm',
    dataAttr,
    placement = 'bottom-start',
    yearPickerEnabled = true,
    viewAllYearsAriaLabel = 'View all years',
    viewYearAriaLabel = 'View year {{year}}',
    nextMonthAriaLabel = 'Next Month',
    prevMonthAriaLabel = 'Previous Month',
    showTodayButton = true,
    showDateText = false,
    dateTextFormatOverride,
    iconFillColorOverride,
    customCalendarComponent,
    onCalendarOpenHanlder = noop,
  } = props;
  const modMinDate: Date | undefined = minDate ? makeSafeDate(minDate) : undefined;
  const modMaxDate: Date | undefined = maxDate ? makeSafeDate(maxDate) : undefined;

  // using state here as the selectedDate value can get stale in handlers
  const [localSelectedDate, setLocalSelectedDate] = useState(selectedDate);

  // activeDate used for keeping track of current date in view for mobile
  const [activeDate, setActiveDate] = useState(selectedDate);
  // keep track of previously passed selected Date for comparison.
  const [prevSelected, setPrevSelected] = useState(
    `${selectedDate.getMonth()}${selectedDate.getDate()}${selectedDate.getFullYear()}`,
  );
  const [mobileModalOpen, setMobileModalOpen] = useState(false);
  const { dark = false, breakpoints = BREAKPOINTS } = useTheme();
  const datepickerRef = useRef<ReactDatePicker>();
  const mobileWidth = typeof isMobile === 'undefined' ? useMedia({ maxWidth: breakpoints[breakpoint] }) : isMobile;

  const [viewYear, setViewYear] = useState<Date | undefined>(undefined);
  const [showYearSelect, setShowYearSelect] = useState(false);

  const [fullYears, setFullYears] = useState<number[]>([]);
  const [miniYears, setMiniYears] = useState<number[]>([]);
  const [decadeYears, setDecadeYears] = useState<DecadeYearsObj[]>([]);

  // es locale defaults to start week on monday so must override
  // If a use specifies a locale object via props, we'll use that in place of lang shortcuts
  // and skip the locale object creation here.
  const locale = localeOverride ?? (lang === 'es' ? { ...es, options: { weekStartsOn: 0 } } : enUS);

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: CustomHeaderProps) => {
    const prevHandlers: Handler = {
      onClick: () => {
        decreaseMonth();
      },
    };
    const nextHandlers: Handler = {
      onClick: () => {
        increaseMonth();
      },
    };
    return (
      <>
        <div className="react-datepicker__navigation--left">
          <Button
            disabled={prevMonthButtonDisabled}
            iconName="carousel-arrow-left"
            iconColor={mlbGrey3}
            shape={ButtonShapes.HOLLOW_BORDERLESS}
            hasHover={false}
            ariaLabel={prevMonthAriaLabel}
            handlers={prevHandlers}
          />
        </div>
        <div className="react-datepicker__current-month">{format(date, 'MMMM', { locale })}</div>
        <div className="react-datepicker__navigation--right">
          <Button
            disabled={nextMonthButtonDisabled}
            shape={ButtonShapes.HOLLOW_BORDERLESS}
            hasHover={false}
            ariaLabel={nextMonthAriaLabel}
            iconName="carousel-arrow-right"
            iconColor={mlbGrey3}
            handlers={nextHandlers}
          />
        </div>
      </>
    );
  };

  // need to update state as new programmatic values come down for selectedDate
  useEffect(() => {
    if (selectedDate) {
      const selectedDateString = `${selectedDate.getMonth()}${selectedDate.getDate()}${selectedDate.getFullYear()}`;
      if (selectedDateString !== prevSelected) {
        setPrevSelected(selectedDateString);
        setLocalSelectedDate(selectedDate);
        setActiveDate(selectedDate);
      }
    }
  }, [selectedDate]);

  // Listens to showYearSelect
  // If true, scrolls to bottom to show current years first
  // Also focuses on the bottom-most year for keyboard nav
  useEffect(() => {
    if (showYearSelect) {
      const yearSelectWrapper: HTMLElement | null = document.getElementById('year-select-wrapper');
      if (yearSelectWrapper) {
        yearSelectWrapper.scrollTop = yearSelectWrapper.scrollHeight;
      }
      const yearPickerEntryId = `yearPicker-${localSelectedDate.getFullYear()}`;
      const yearPickerEntry: HTMLElement | null = document.getElementById(yearPickerEntryId);
      if (yearPickerEntry) {
        yearPickerEntry.focus();
      }
    }
  }, [showYearSelect]);

  useEffect(() => {
    if (viewYear && datepickerRef && datepickerRef.current) {
      // Hacky? Maybe. Gets the job of setting keyboard entry after manual year change? Yes.
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      datepickerRef.current.setPreSelection(viewYear);
    }
  }, [viewYear]);

  // Sets full years and decade years list whenever min and max dates change
  useEffect(() => {
    const { fullYears, decadeYears } = generateFullYears(localSelectedDate, modMinDate, modMaxDate);
    setFullYears(fullYears);
    setDecadeYears(decadeYears);
  }, [maxDate, minDate]);

  // Sets mini years whenever active date changes or fullYears list changes
  useEffect(() => {
    setMiniYears(generateMiniYears(fullYears, activeDate, modMinDate, modMaxDate));
  }, [localSelectedDate, fullYears]);

  // Sets the currently viewed year when selected in year select
  const setYear = (event: Event, value: number) => {
    const year = value;
    const newDate = new Date(activeDate);
    newDate.setFullYear(year);
    setShowYearSelect(false);
    setActiveDate(newDate);
    setViewYear(newDate);
  };

  const onChangeHandler: Handler = {
    onChange: (date: Date) => {
      setViewYear(undefined);
      setLocalSelectedDate(date);
    },
  };
  const onTodayClickHandler: Handler = {
    onClick: () => {
      const todaysDate = new Date();
      setLocalSelectedDate(todaysDate);
      setActiveDate(todaysDate);
      setMobileModalOpen(false);

      // the date picker needs an onChange while our button needs on click so sort of hacking it together here
      if (handlers.onChange) {
        handlers.onChange(todaysDate);
      }

      datepickerRef.current?.setOpen(false);
    },
  };
  const combinedHandlers: Handler = bindHandlers(null, handlers, onChangeHandler);

  const fillColor: string = fetchIconFillColor(disabled, dark, iconFillColorOverride);

  const CalendarIcon = customCalendarComponent ?? <Icon name="calendar-2" fill={fillColor} size={triggerSize} />;

  const onBtnFocus = (event: React.SyntheticEvent<EventTarget>) => {
    event.preventDefault();
    return;
  };

  // Key navigation fails using setOpen or customInput without an input element
  // hacked this together slightly to have a hidden input there so it works
  // using forwardRef as it was throwing a console err otherwise (I don't know why...)
  type DatePickerInputProps = {
    formattedDate: string;
    showDate: boolean;
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const DatepickerInput = forwardRef<HTMLElement, DatePickerInputProps>((props, ref) => {
    const { formattedDate, showDate } = props;
    return (
      <React.Fragment>
        {/* would have liked to use a button here, but it does not work as a child of a label that uses for / id link  */}
        <InputWrapper
          type="text"
          {...props}
          id="pickerInput"
          onFocus={(e: SyntheticEvent<EventTarget, Event>) => onBtnFocus(e)}
          fill={fillColor}
          showDate={showDate}
        />
        <InputLabel htmlFor="pickerInput" disabled={disabled} showDate={showDate}>
          {CalendarIcon}
          {showDate ? <DateLabel>{formattedDate}</DateLabel> : null}
        </InputLabel>
      </React.Fragment>
    );
  });

  const TodayButtonWrapper = mobileWidth ? MobileButtonWrapper : ButtonWrapper;
  const TodayButton = (
    <TodayButtonWrapper>
      <Button
        shape={ButtonShapes.HOLLOW}
        handlers={onTodayClickHandler}
        size={mobileWidth ? ButtonSizes.MEDIUM : ButtonSizes.SMALL}
      >
        {todayLabel}
      </Button>
    </TodayButtonWrapper>
  );

  const daysLabels = daysHeaderLabels ?? null;
  let DatePickerWrapperComponent: React.ComponentType<DPWrapperProps> = DatePickerWrapper;

  if (daysLabels && daysLabels.length === 7) {
    DatePickerWrapperComponent = DatePickerWrapperCustomDays;
  }

  const sharedWrapperProps: DPWrapperProps = {
    daysLabels,
    dark: !!dark,
    openLeft,
    isMobile: mobileWidth,
    openLeftMinBreakpoint: breakpoints[openLeftMin] || openLeftMin,
    openLeftMaxBreakpoint: (openLeftMax && breakpoints[openLeftMax]) || openLeftMax,
  };

  const formattedLocalDate = localSelectedDate
    ? format(localSelectedDate, dateTextFormatOverride || (lang === 'es' ? textDateDisplayEs : textDateDisplayEn), {
        locale,
      })
    : '';

  const sharedDatePickerProps: SharedDatePickerProps = {
    maxDate: modMaxDate,
    minDate: modMinDate,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    locale,
    excludeDates: excludedDates,
    includeDates: includedDates,
    shouldCloseOnSelect: closeOnSelect,
    renderCustomHeader,
    disabled,
    formatWeekDay: (day: string) => day?.substring(0, 1),
  };

  // Retrieve the current year and the actively viewed year
  const currentYear = new Date().getFullYear();
  const activeYear = activeDate.getFullYear();

  // Update the active date when months change for accurate representation
  // in year selects
  const monthChangeHandler = (date: Date) => {
    setActiveDate(date);
  };

  // Handler for year picker buttons
  const yearPickerHandler: Handler = {
    onClick: (e: Event, v: number) => {
      setYear(e, v);
      const newDate = new Date(activeDate);
      newDate.setFullYear(v);
      setMiniYears(generateMiniYears(fullYears, newDate, modMinDate, modMaxDate));
    },
  };

  // The full-sized year picker accessed from kebab button
  // map uses createYearRow function to make list of decades
  const YearPicker: ReactElement = yearPickerEnabled ? (
    <YearSelectWrapper id="year-select-wrapper" showYearSelect={showYearSelect} dark={dark}>
      {decadeYears.map((fiveYearObj, i: number) =>
        createYearRow(fiveYearObj, yearPickerHandler, i, currentYear, activeYear, dark, viewYearAriaLabel),
      )}
    </YearSelectWrapper>
  ) : (
    <div />
  );

  // The mini year picker
  // Shows kebab icon to open full year picker
  const MiniYearPicker: ReactElement = yearPickerEnabled ? (
    <MiniYearWrapper mobileBreakpoint={breakpoints[breakpoint]}>
      {fullYears.length > 5 && (
        <MiniYearButtonWrapper dark={dark}>
          <Button
            iconName="kebab"
            iconColor={mlbGrey3}
            shape={ButtonShapes.HOLLOW_BORDERLESS}
            hasHover={false}
            ariaLabel={viewAllYearsAriaLabel}
            handlers={{
              onClick: () => {
                setShowYearSelect(true);
              },
            }}
          />
        </MiniYearButtonWrapper>
      )}

      {miniYears.map((year) => (
        <MiniYearButtonWrapper
          isCurrentYear={year === currentYear}
          isActiveYear={year === activeYear}
          dark={dark}
          key={`miniYearButtonWrapper-${year}`}
        >
          <Button
            shape={ButtonShapes.HOLLOW_BORDERLESS}
            value={year}
            hasHover={false}
            ariaLabel={injectYearString(viewYearAriaLabel, year)}
            handlers={{
              onClick: (e: Event, v: number) => setYear(e, v),
            }}
          >
            {year}
          </Button>
        </MiniYearButtonWrapper>
      ))}
    </MiniYearWrapper>
  ) : (
    <div />
  );

  // renderCustomHeader can be used eventually if we want custom header for datepicker, but for now slight deviation approved by product
  return (
    <>
      <DatePickerStyle />
      {mobileWidth ? (
        <MobileDatePicker
          {...props}
          formattedLocalDate={formattedLocalDate}
          CalendarIcon={CalendarIcon}
          DatePickerWrapperComponent={DatePickerWrapperComponent}
          sharedWrapperProps={sharedWrapperProps}
          TodayButton={TodayButton}
          activeDate={activeDate}
          setActiveDate={setActiveDate}
          localSelectedDate={localSelectedDate}
          combinedHandlers={combinedHandlers}
          mobileModalOpen={mobileModalOpen}
          setMobileModalOpen={setMobileModalOpen}
          YearPicker={YearPicker}
          MiniYearPicker={MiniYearPicker}
          viewYear={viewYear}
          setViewYear={setViewYear}
          showTodayButton={showTodayButton}
          {...dataAttr}
          sharedDatePickerProps={sharedDatePickerProps}
          showDateText={showDateText}
        />
      ) : (
        <DatePickerWrapperComponent {...sharedWrapperProps} {...dataAttr}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <ReactDatePicker
            selected={localSelectedDate}
            onChange={(date: Date) => combinedHandlers.onChange(date)}
            inline={inline}
            ref={datepickerRef}
            popperPlacement={placement}
            customInput={<DatepickerInput showDate={showDateText} formattedDate={formattedLocalDate} />}
            onCalendarOpen={() => {
              onCalendarOpenHanlder(datepickerRef);
            }}
            onCalendarClose={() => {
              setViewYear(undefined);
              setShowYearSelect(false);
              setActiveDate(localSelectedDate);
              setMiniYears(generateMiniYears(fullYears, localSelectedDate, modMinDate, modMaxDate));
            }}
            openToDate={viewYear}
            onMonthChange={monthChangeHandler}
            popperModifiers={[
              {
                name: 'offset',
                options: {
                  offset: [0, 0],
                },
              },
              {
                name: 'preventOverflow',
                options: {
                  rootBoundary: 'viewport',
                  tether: false,
                  altAxis: true,
                },
              },
            ]}
            {...sharedDatePickerProps}
          >
            {YearPicker}

            {MiniYearPicker}

            {showTodayButton ? TodayButton : null}
          </ReactDatePicker>
        </DatePickerWrapperComponent>
      )}
    </>
  );
};

export default DatePicker;
